import type {
  OptionType,
  StorefrontProductEmailReminderKind,
  StorefrontProductEmailRemindersSchema,
} from "../../../../../types/types";
import axios from "axios";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import { useNotifications } from "../../../../../components/Notifications/NotificationsContext";
import { useFormWrapper, useStoreState } from "../../../../../util/util";
import {
  REMINDER_SELECT_BOX_OPTIONS,
  ReminderGrid,
} from "./ReminderNotifications";
import type { FieldError, FormState } from "react-hook-form";
import { Controller } from "react-hook-form";
import { PrimaryButtonMedium } from "../../../../../components/Buttons/Buttons";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { ToggleSwitchV2 } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import { H6 } from "../../../../../components/Typography/Typography";
import { match } from "ts-pattern";
import { spacingTokens } from "../../../../../designTokens";

type ProductReminderFormValues = Record<
  | `${StorefrontProductEmailReminderKind}_first`
  | `${StorefrontProductEmailReminderKind}_second`,
  boolean | null
> &
  Record<
    StorefrontProductEmailReminderKind,
    {
      first_reminder: OptionType<number> | null;
      second_reminder: OptionType<number> | null;
    } | null
  >;

type ReminderErrorType = Record<
  "first_reminder" | "second_reminder",
  FieldError | undefined
>;

// Define the actual shape returned by watch() for the specified fields
type ProductNotificationsWatchedValues = {
  // Use specific keys based on reminderType = "asset_expiry"
  asset_expiry_first?: boolean | null;
  asset_expiry_second?: boolean | null;
  "asset_expiry.first_reminder"?: OptionType<number> | null;
  "asset_expiry.second_reminder"?: OptionType<number> | null;
};

function getTitleForProductReminderType(
  t: TFunction<"translation", undefined>,
  reminder_type: StorefrontProductEmailReminderKind
): string {
  return match(reminder_type)
    .with("asset_expiry", () => t("Assets Expiring"))
    .with("price_tier_expiry", () => t("Price Tiers Expiring"))
    .exhaustive();
}

export function ProductNotificationsForm({
  productReminders,
  onSuccess,
}: {
  productReminders: StorefrontProductEmailRemindersSchema;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useNotifications();
  const { storefront_id } = useStoreState();

  const reminderType: StorefrontProductEmailReminderKind = "asset_expiry";

  const defaultValues: ProductReminderFormValues =
    productReminders.reminder_data.reduce<ProductReminderFormValues>(
      (acc, reminder) => {
        if (reminder.reminder_type === reminderType) {
          const { first_reminder, second_reminder } = reminder;
          // Note: API is_enabled is based on first_reminder existing
          acc[`${reminderType}_first`] = Boolean(first_reminder);
          acc[`${reminderType}_second`] = Boolean(second_reminder);
          acc[reminderType] =
            first_reminder || second_reminder
              ? {
                  first_reminder: first_reminder
                    ? REMINDER_SELECT_BOX_OPTIONS.find(
                        ({ value }) => value === first_reminder
                      ) ?? null
                    : null,
                  second_reminder: second_reminder
                    ? REMINDER_SELECT_BOX_OPTIONS.find(
                        ({ value }) => value === second_reminder
                      ) ?? null
                    : null,
                }
              : null;
        }
        return acc;
        // Initialize with default structure if no data exists for reminderType
      },
      {
        [`${reminderType}_first`]: false,
        [`${reminderType}_second`]: false,
        [reminderType]: null,
      } as ProductReminderFormValues
    );

  const {
    control,
    handleSubmit,
    formState,
    watch,
    register,
    setValue,
    clearErrors,
  } = useFormWrapper<ProductReminderFormValues>({
    defaultValues,
    shouldUnregister: true,
  });
  const { errors, isSubmitting } = formState;

  const fieldsToWatch = [
    `${reminderType}_first`,
    `${reminderType}_second`,
    `${reminderType}.first_reminder`,
    `${reminderType}.second_reminder`,
  ];
  const watchedFormValues: ProductNotificationsWatchedValues =
    watch(fieldsToWatch);

  const isFirstReminderEnabled = watchedFormValues[`${reminderType}_first`];
  const isSecondReminderEnabled = watchedFormValues[`${reminderType}_second`];
  const firstReminderOption =
    watchedFormValues[`${reminderType}.first_reminder`];
  const secondReminderOption =
    watchedFormValues[`${reminderType}.second_reminder`];

  const onSubmit = async (values: ProductReminderFormValues) => {
    const reminderDetails = values[reminderType];

    const putPayload = {
      reminder_type: reminderType,
      // is_enabled in API reflects if the first reminder is active
      is_enabled: values[`${reminderType}_first`] ?? false,
      first_reminder:
        values[`${reminderType}_first`] && reminderDetails?.first_reminder
          ? reminderDetails.first_reminder.value
          : null,
      second_reminder:
        values[`${reminderType}_second`] && reminderDetails?.second_reminder
          ? reminderDetails.second_reminder.value
          : null,
    };

    try {
      await axios.put(`/v2/storefronts/${storefront_id}/product-reminders`, {
        reminder_data: [putPayload],
      });

      notifySuccess(t("Product reminders updated successfully"));
      onSuccess();
    } catch (error) {
      console.error("Failed to update product reminders:", error);
      notifyError(t("Failed to update product reminders. Please try again."));
    }
  };

  const isReminderOrderInvalid =
    isSecondReminderEnabled &&
    !!firstReminderOption?.value &&
    !!secondReminderOption?.value &&
    firstReminderOption.value <= secondReminderOption.value;

  const firstReminderDateErrorMsg = isReminderOrderInvalid
    ? { message: t("Must be sent before the second reminder") }
    : null;

  const secondReminderDateErrorMsg = isReminderOrderInvalid
    ? { message: t("Must be sent after the first reminder") }
    : null;

  const requiredFirstReminderError =
    (errors?.[`${reminderType}`] as unknown as ReminderErrorType)
      ?.first_reminder?.type === "required"
      ? { message: "Required" }
      : null;

  const requiredSecondReminderError =
    (errors?.[`${reminderType}`] as unknown as ReminderErrorType)
      ?.second_reminder?.type === "required"
      ? { message: "Required" }
      : null;

  // Calculate options for the second dropdown
  const optionsForSecondSelect = REMINDER_SELECT_BOX_OPTIONS.filter(
    (option) => option.value < (firstReminderOption?.value ?? Infinity)
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <H6 style={{ marginBottom: spacingTokens.spacing_2xl }}>
        {getTitleForProductReminderType(t, reminderType)}
      </H6>
      <ReminderGrid style={{ marginBottom: "30px" }}>
        <ToggleSwitchV2
          label={t("First Reminder")}
          name={`${reminderType}_first`}
          ref={register({ required: false })}
          defaultChecked={defaultValues[`${reminderType}_first`] ?? false}
          onChange={(e) => {
            if (!e.target.checked) {
              // Clear first and second select boxes + second toggle
              setValue(`${reminderType}.first_reminder`, null);
              setValue(`${reminderType}.second_reminder`, null);
              setValue(`${reminderType}_first`, false);
              setValue(`${reminderType}_second`, false, {
                shouldValidate: true,
                shouldDirty: true,
              });
              clearErrors([
                `${reminderType}.first_reminder`,
                `${reminderType}.second_reminder`,
              ]);
            }
          }}
        />
        <Controller
          name={`${reminderType}.first_reminder`}
          as={SelectBoxV2}
          control={control}
          rules={{
            required: Boolean(isFirstReminderEnabled),
            validate: () =>
              !isReminderOrderInvalid ||
              (t("Must be sent before the second reminder") as string),
          }}
          placeholder={t("Send before")}
          options={REMINDER_SELECT_BOX_OPTIONS}
          defaultValue={defaultValues[reminderType]?.first_reminder}
          error={
            firstReminderDateErrorMsg ??
            (errors[`${reminderType}`] as ReminderErrorType | undefined)
              ?.first_reminder ??
            requiredFirstReminderError
          }
          isDisabled={!isFirstReminderEnabled}
          clearable={true}
          errors={errors}
          formState={
            {
              submitCount: (formState as any).submitCount || 0,
            } as unknown as FormState<ProductReminderFormValues>
          }
        />
        <ToggleSwitchV2
          label={t("Second Reminder")}
          name={`${reminderType}_second`}
          ref={register({ required: false })}
          defaultChecked={defaultValues[`${reminderType}_second`] ?? false}
          disabled={!isFirstReminderEnabled}
          onChange={(e) => {
            if (!e.target.checked) {
              setValue(`${reminderType}.second_reminder`, null);
              clearErrors(`${reminderType}.second_reminder`);
            }
          }}
        />
        <Controller
          name={`${reminderType}.second_reminder`}
          as={SelectBoxV2}
          control={control}
          rules={{
            required: Boolean(isSecondReminderEnabled),
            validate: () =>
              !isReminderOrderInvalid ||
              (t("Must be sent after the first reminder") as string),
          }}
          placeholder={t("Send before")}
          options={optionsForSecondSelect}
          defaultValue={defaultValues[reminderType]?.second_reminder}
          error={
            secondReminderDateErrorMsg ??
            (errors[`${reminderType}`] as ReminderErrorType | undefined)
              ?.second_reminder ??
            requiredSecondReminderError
          }
          isDisabled={!isSecondReminderEnabled || !isFirstReminderEnabled}
          clearable={true}
          errors={errors}
          formState={
            {
              submitCount: (formState as any).submitCount || 0,
            } as unknown as FormState<ProductReminderFormValues>
          }
        />
      </ReminderGrid>

      <PrimaryButtonMedium
        type="submit"
        loading={isSubmitting}
        disabled={
          isSubmitting ||
          ((isFirstReminderEnabled ?? false) && !firstReminderOption) ||
          ((isSecondReminderEnabled ?? false) && !secondReminderOption)
        }
      >
        {t("Save your changes")}
      </PrimaryButtonMedium>
    </form>
  );
}
